'use client'

import { createContext, useCallback, useContext, useMemo, useRef } from 'react'

import { logEvent } from '@/shared/external/firebase'

import { MODOOCAR_CODE, MODOOCAR_EVENT } from '../_constants'

import type { ReactNode } from 'react'

type TTrackParams = Record<string, string | number | boolean | null>

interface IModoocarTrackingContextValue {
  target: string
  mode: string
}

const ModoocarTrackingContext =
  createContext<IModoocarTrackingContextValue | null>(null)

interface IModoocarTrackingProviderProps {
  company: string
  modelName: string
  fuelType: string
  children: ReactNode
}

export const ModoocarTrackingProvider = ({
  company,
  modelName,
  fuelType,
  children,
}: IModoocarTrackingProviderProps) => {
  const value = useMemo<IModoocarTrackingContextValue>(
    () => ({ target: `${company} / ${modelName}`, mode: fuelType }),
    [company, modelName, fuelType]
  )

  return (
    <ModoocarTrackingContext.Provider value={value}>
      {children}
    </ModoocarTrackingContext.Provider>
  )
}

export const useModoocarTracking = () => {
  const ctx = useContext(ModoocarTrackingContext)

  return useMemo(() => {
    const track = (eventName: string, params: TTrackParams = {}) =>
      logEvent(eventName, {
        ...params,
        target: ctx?.target ?? null,
        mode: ctx?.mode ?? null,
      })

    return {
      track,
      pageShow: () =>
        track(MODOOCAR_EVENT.PAGE_SHOW, {
          code: MODOOCAR_CODE.PAGE,
          type: 'page',
          label: '모두의 차고 상세페이지',
        }),
      sectionShow: (code: string, label: string) =>
        track(MODOOCAR_EVENT.SECTION_SHOW, { code, type: 'section', label }),
      itemShow: (
        code: string,
        type: string,
        label: string,
        extra: TTrackParams = {}
      ) => track(MODOOCAR_EVENT.ITEM_SHOW, { code, type, label, ...extra }),
      itemClick: (
        code: string,
        type: string,
        label: string,
        extra: TTrackParams = {}
      ) => track(MODOOCAR_EVENT.ITEM_CLICK, { code, type, label, ...extra }),
      itemChange: (
        code: string,
        type: string,
        label: string,
        extra: TTrackParams = {}
      ) => track(MODOOCAR_EVENT.ITEM_CHANGE, { code, type, label, ...extra }),
      logoClick: () =>
        track(MODOOCAR_EVENT.LOGO_CLICK, {
          code: MODOOCAR_CODE.HEADER,
          type: 'logo',
          label: '마이클 로고',
        }),
    }
  }, [ctx])
}

export const useModoocarImpression = <T extends HTMLElement = HTMLDivElement>(
  onImpress: () => void,
  options?: { enabled?: boolean; threshold?: number }
) => {
  const firedRef = useRef(false)
  const callbackRef = useRef(onImpress)
  callbackRef.current = onImpress

  const enabled = options?.enabled ?? true
  const threshold = options?.threshold ?? 0.5

  return useCallback(
    (node: T | null) => {
      if (!node || !enabled || firedRef.current) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true
            callbackRef.current()
            observer.disconnect()
          }
        },
        { threshold }
      )

      observer.observe(node)
    },
    [enabled, threshold]
  )
}
